import { cur } from '@/lib/currency';
import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ReportHeader } from '@/components/reports/ReportUI';
import { ReportFilterBar, useReportFilters } from '@/components/reports/ReportFilterBar';
import { ExportTrio } from '@/components/accounting/ExportTrio';
import { TrendingDown } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function PurchasesVATReportPage() {
  const [data, setData] = useState(null);
  const f = useReportFilters({ withUnassigned: true });
  const dateFilter = f.range;
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFilter.start) params.start_date = dateFilter.start;
      if (dateFilter.end) params.end_date = dateFilter.end;
      if (f.branchIds.length) params.branch_ids = f.branchIds.join(',');
      const res = await api.get('/accounting/purchases-vat-report', { params });
      setData(res.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [f.params]);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="purchases-vat-report-page">
        <ReportHeader icon={TrendingDown} title="Purchases VAT Report" subtitle="VAT paid on purchases and bills" testId="pvat-header" />
        <ReportFilterBar testId="pvat-filter" branches={f.branches} branchIds={f.branchIds} onBranchIds={f.setBranchIds}
          range={f.range} onRange={f.setRange}>
          <ExportTrio spec={() => ({ title: 'Purchases VAT Report', subtitle: `${f.range.start} → ${f.range.end} · ${f.branchLabel}`, filename: 'purchases_vat_report', rows: data?.rows || [], cols: [{ key: 'date', label: 'Date', format: (v) => String(v || '').slice(0, 10) }, { key: 'bill_number', label: 'Bill / Ref' }, { key: 'supplier', label: 'Supplier' }, { key: 'net_amount', label: 'Net', numeric: true }, { key: 'vat_rate', label: 'VAT %', numeric: true }, { key: 'vat_amount', label: 'VAT', numeric: true }, { key: 'total', label: 'Total', numeric: true }] })} disabled={!data} testId="pvat-export" />
        </ReportFilterBar>

        {data && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Card><CardContent className="pt-5"><p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Net Purchases</p><p className="text-2xl font-bold text-foreground">{cur()} {data.summary?.total_net_purchases?.toLocaleString()}</p></CardContent></Card>
            <Card className="border-error/30 bg-error/10"><CardContent className="pt-5"><p className="text-[10px] uppercase tracking-wider text-error font-semibold">VAT Paid</p><p className="text-2xl font-bold text-error">{cur()} {data.summary?.total_vat_paid?.toLocaleString()}</p></CardContent></Card>
            <Card><CardContent className="pt-5"><p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Total with VAT</p><p className="text-2xl font-bold text-foreground">{cur()} {data.summary?.total_with_vat?.toLocaleString()}</p></CardContent></Card>
            <Card><CardContent className="pt-5"><p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Taxable Bills</p><p className="text-2xl font-bold">{data.summary?.taxable_transactions} <span className="text-sm font-normal text-muted-foreground">of {data.summary?.total_transactions}</span></p></CardContent></Card>
          </div>
        )}

        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 border-b"><tr>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Date</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Bill #</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Supplier</th>
                  <th className="text-right px-4 py-3 font-medium text-muted-foreground">Net Amount</th>
                  <th className="text-right px-4 py-3 font-medium text-muted-foreground">VAT %</th>
                  <th className="text-right px-4 py-3 font-medium text-error font-semibold">VAT Amount</th>
                  <th className="text-right px-4 py-3 font-medium text-muted-foreground">Total</th>
                </tr></thead>
                <tbody>
                  {loading ? <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">Loading...</td></tr> :
                    data?.rows?.map((r, i) => (
                    <tr key={i} className="border-b hover:bg-muted/50">
                      <td className="px-4 py-3 text-muted-foreground">{r.date?.slice(0, 10)}</td>
                      <td className="px-4 py-3 font-mono text-xs">{r.bill_number}</td>
                      <td className="px-4 py-3">{r.supplier}</td>
                      <td className="px-4 py-3 text-right">{cur()} {r.net_amount?.toLocaleString()}</td>
                      <td className="px-4 py-3 text-right"><Badge className="bg-muted text-muted-foreground text-[10px]">{r.vat_rate}%</Badge></td>
                      <td className="px-4 py-3 text-right font-bold text-error">{cur()} {r.vat_amount?.toLocaleString()}</td>
                      <td className="px-4 py-3 text-right font-medium">{cur()} {r.total?.toLocaleString()}</td>
                    </tr>
                  ))}
                  {!loading && (!data?.rows || data.rows.length === 0) && <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">No taxable purchases found</td></tr>}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
